import { useEffect, useState } from "react";
import StaffLayout from "@/components/staff/StaffLayout";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { RefreshCw, Loader2, AlertCircle, CheckCircle2, LogIn, LogOut, Radio, Calendar, TrendingUp, Wallet, Undo2, AlertTriangle } from "lucide-react";

function StatCard({ label, value, hint, accent, icon: Icon }) {
  return (
    <div className="bg-white border border-[#E4E4E7] rounded-md p-5">
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-[0.25em] text-[#71717A]">{label}</div>
        {Icon && <Icon size={14} className="text-[#A1A1AA]" />}
      </div>
      <div className={`mt-2 font-display text-3xl ${accent || ""}`}>{value}</div>
      {hint && <div className="text-xs text-[#71717A] mt-1">{hint}</div>}
    </div>
  );
}

function fmtEUR(v) {
  return "€" + Math.round(Number(v) || 0).toLocaleString();
}

function BookingRow({ b }) {
  return (
    <div className="flex items-center justify-between border-b border-[#F4F4F5] last:border-0 py-2.5 px-3 hover:bg-[#FAFAFA]" data-testid={`channel-booking-${b.id}`}>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium truncate">{b.guest_name || "External"}</div>
        <div className="text-xs text-[#71717A] truncate">{b.room_name} · {b.check_in} → {b.check_out}</div>
      </div>
      <div className="flex items-center gap-2">
        {b.source === "external" && <span className="text-[10px] px-1.5 py-0.5 bg-violet-100 text-violet-700 rounded">OTA</span>}
        {b.source === "staff" && <span className="text-[10px] px-1.5 py-0.5 bg-emerald-100 text-emerald-700 rounded">STAFF</span>}
        {(!b.source || b.source === "website") && <span className="text-[10px] px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded">SITE</span>}
        <div className="text-xs text-[#71717A] hidden sm:block">€{(b.total || 0).toFixed(0)}</div>
      </div>
    </div>
  );
}

export default function StaffChannelsPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const r = await api.get("/admin/channel-overview");
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load");
    } finally { setLoading(false); }
  }
  useEffect(() => { load(); const id = setInterval(load, 60_000); return () => clearInterval(id); }, []);

  async function syncAll() {
    setSyncing(true);
    try {
      const r = await api.post("/admin/ical/sync-all");
      const created = (r.data.results || []).reduce((s, x) => s + (x.created || 0), 0);
      const errors = (r.data.results || []).filter((x) => x.error).length;
      toast.success(`Synced ${r.data.synced} room(s) · ${created} new booking(s)${errors ? ` · ${errors} error(s)` : ""}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Sync failed");
    } finally { setSyncing(false); }
  }

  if (loading || !data) return <StaffLayout><div className="flex items-center gap-2 text-[#71717A]"><Loader2 className="animate-spin" size={16} /> Loading channel overview…</div></StaffLayout>;

  const { arrivals = [], departures = [], upcoming_7d = [], recent_ota = [], source_totals = {}, ical_rooms = [], revenue = {} } = data;
  const totalSite = source_totals.website || 0;
  const totalOTA = source_totals.external || 0;
  const totalStaff = source_totals.staff || 0;

  return (
    <StaffLayout>
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#71717A]">{data.today}</div>
          <h1 className="font-display text-3xl sm:text-4xl mt-1">Channel manager</h1>
          <p className="text-sm text-[#71717A] mt-1">Today at a glance — your website, OTAs, and staff bookings in one place.</p>
        </div>
        <button
          data-testid="channel-sync-all"
          onClick={syncAll}
          disabled={syncing}
          className="inline-flex items-center gap-2 bg-brand-primary text-white text-sm px-4 py-2 rounded-md hover:bg-brand-primaryHover disabled:opacity-50"
        >
          {syncing ? <Loader2 className="animate-spin" size={14} /> : <RefreshCw size={14} />}
          Sync all OTA feeds
        </button>
      </div>

      {/* Revenue widgets */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-3">
        <StatCard
          label="Collected today"
          value={fmtEUR(revenue.today_collected)}
          hint="Paid bookings & cash today"
          icon={Wallet}
          accent="text-emerald-700"
        />
        <StatCard
          label="Next 7 days"
          value={fmtEUR(revenue.upcoming_7d)}
          hint={`${upcoming_7d.length} booking${upcoming_7d.length !== 1 ? "s" : ""} arriving`}
          icon={TrendingUp}
          accent="text-brand-primary"
        />
        <StatCard
          label="Month-to-date"
          value={fmtEUR(revenue.month_to_date)}
          hint="All paid revenue this month"
          icon={Calendar}
        />
        <StatCard
          label="Refunds · 30d"
          value={fmtEUR(revenue.refunds_30d)}
          hint={revenue.refunds_30d > 0 ? "Issued via SumUp" : "No refunds this month"}
          icon={Undo2}
          accent={revenue.refunds_30d > 0 ? "text-violet-700" : ""}
        />
      </div>

      {revenue.unpaid_count > 0 && (
        <div className="mb-6 bg-amber-50 border border-amber-200 rounded-md px-4 py-3 text-sm flex items-center gap-3" data-testid="unpaid-warning">
          <AlertTriangle size={16} className="text-amber-700 flex-shrink-0" />
          <div className="flex-1">
            <strong>{revenue.unpaid_count}</strong> active booking{revenue.unpaid_count !== 1 ? "s" : ""} unpaid — <strong>{fmtEUR(revenue.unpaid_total)}</strong> outstanding.
          </div>
          <a href="/staff/bookings" className="text-xs font-medium text-amber-800 hover:underline">View →</a>
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <StatCard label="Arrivals today" value={arrivals.length} accent="text-emerald-700" icon={LogIn} />
        <StatCard label="Departures today" value={departures.length} accent="text-amber-700" icon={LogOut} />
        <StatCard label="Next 7 days" value={upcoming_7d.length} icon={Calendar} />
        <StatCard label="OTA last 24h" value={recent_ota.length} accent="text-violet-700" icon={Radio} />
      </div>

      <div className="grid lg:grid-cols-2 gap-5 mb-6">
        <div className="bg-white border border-[#E4E4E7] rounded-md">
          <div className="px-4 py-3 border-b border-[#E4E4E7] flex items-center gap-2 text-sm font-medium"><LogIn size={14} className="text-emerald-700" /> Arriving today ({arrivals.length})</div>
          {arrivals.length ? arrivals.map((b) => <BookingRow key={b.id} b={b} />) : <div className="px-4 py-6 text-xs text-[#71717A]">No check-ins today.</div>}
        </div>
        <div className="bg-white border border-[#E4E4E7] rounded-md">
          <div className="px-4 py-3 border-b border-[#E4E4E7] flex items-center gap-2 text-sm font-medium"><LogOut size={14} className="text-amber-700" /> Checking out today ({departures.length})</div>
          {departures.length ? departures.map((b) => <BookingRow key={b.id} b={b} />) : <div className="px-4 py-6 text-xs text-[#71717A]">No check-outs today.</div>}
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-5 mb-6">
        <div className="bg-white border border-[#E4E4E7] rounded-md">
          <div className="px-4 py-3 border-b border-[#E4E4E7] flex items-center gap-2 text-sm font-medium"><Calendar size={14} /> Upcoming · next 7 days ({upcoming_7d.length})</div>
          <div className="max-h-72 overflow-auto">
            {upcoming_7d.length ? upcoming_7d.map((b) => <BookingRow key={b.id} b={b} />) : <div className="px-4 py-6 text-xs text-[#71717A]">Nothing scheduled.</div>}
          </div>
        </div>
        <div className="bg-white border border-[#E4E4E7] rounded-md">
          <div className="px-4 py-3 border-b border-[#E4E4E7] flex items-center gap-2 text-sm font-medium"><Radio size={14} className="text-violet-700" /> OTA bookings · last 24h ({recent_ota.length})</div>
          <div className="max-h-72 overflow-auto">
            {recent_ota.length ? recent_ota.map((b) => <BookingRow key={b.id} b={b} />) : <div className="px-4 py-6 text-xs text-[#71717A]">No fresh OTA bookings.</div>}
          </div>
        </div>
      </div>

      {/* Source breakdown */}
      <div className="bg-white border border-[#E4E4E7] rounded-md p-5 mb-6">
        <div className="text-sm font-medium mb-3">All-time booking sources</div>
        <div className="flex flex-wrap gap-4">
          <div className="flex items-center gap-2"><span className="w-3 h-3 bg-amber-500 rounded-sm" /><span className="text-sm">Website · <strong>{totalSite}</strong></span></div>
          <div className="flex items-center gap-2"><span className="w-3 h-3 bg-violet-500 rounded-sm" /><span className="text-sm">OTA · <strong>{totalOTA}</strong></span></div>
          <div className="flex items-center gap-2"><span className="w-3 h-3 bg-emerald-500 rounded-sm" /><span className="text-sm">Staff · <strong>{totalStaff}</strong></span></div>
        </div>
      </div>

      {/* iCal feeds */}
      <div className="bg-white border border-[#E4E4E7] rounded-md">
        <div className="px-4 py-3 border-b border-[#E4E4E7] text-sm font-medium">iCal feeds</div>
        {ical_rooms.length === 0 && <div className="px-4 py-6 text-xs text-[#71717A]">No iCal import URLs set. Add them in Rooms → edit room.</div>}
        {ical_rooms.map((r) => (
          <div key={r.id} className="px-4 py-3 border-b border-[#F4F4F5] last:border-0 flex flex-wrap items-center gap-3" data-testid={`ical-row-${r.id}`}>
            <div className="flex-1 min-w-[200px]">
              <div className="text-sm font-medium">{r.name}</div>
              <div className="text-xs text-[#71717A] truncate max-w-[400px]">{r.ical_import_url}</div>
            </div>
            <div className="text-xs text-[#71717A]">{r.external_count || 0} OTA bookings</div>
            {r.ical_last_status === "ok" ? (
              <span className="inline-flex items-center gap-1 text-emerald-700 text-xs"><CheckCircle2 size={12} /> Synced</span>
            ) : r.ical_last_status === "error" ? (
              <span className="inline-flex items-center gap-1 text-rose-700 text-xs" title={r.ical_last_error}><AlertCircle size={12} /> Error</span>
            ) : (
              <span className="text-xs text-[#71717A]">Never synced</span>
            )}
            <div className="text-xs text-[#71717A] min-w-[140px] text-right">{r.ical_last_sync ? new Date(r.ical_last_sync).toLocaleString() : "—"}</div>
          </div>
        ))}
      </div>
    </StaffLayout>
  );
}
